import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, onSnapshot, collection, query, where, limit, getDocs, updateDoc, getDoc } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import app from "../../firebase";
import { useSession } from "../../context/SessionContext";
import { clearEnrolledSession, readEnrolledSession, readEnrolledStudentData, writeEnrolledStudentData } from "../../auth/roleSession";
import { buildEnrollmentWindows, formatCalendarDate, useCalendarClock } from "../../utils/announcementDates";

export default function EnrolledDashboard({ student: propStudent }) {
  const todayKey = useCalendarClock();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { currentUser, loading } = useSession();
  
  // Get student from props, state, or sessionStorage (for enrolled portal login)
  let initialStudent = propStudent || state?.student || {};
  if (!initialStudent.id && !initialStudent.firstName) {
    try {
      const storedStudent = readEnrolledStudentData();
      const stored = storedStudent ? JSON.stringify(storedStudent) : null;
      if (stored) {
        initialStudent = JSON.parse(stored);
        console.log("[EnrolledDashboard] Loaded student from sessionStorage:", initialStudent);
      }
    } catch (e) {
      console.error("[EnrolledDashboard] Error loading student from sessionStorage:", e);
    }
  }
  
  const student = initialStudent || {};
  const [localStudent, setLocalStudent] = useState(student);

  // Redirect to /enrolled/signin when user logs out or session is missing
  // Wait for loading to complete before checking
  useEffect(() => {
    if (!loading) {
      const enrolledSession = readEnrolledSession();
      const isEnrolledLoggedIn = !!enrolledSession?.studentId;
      const hasEnrolledSession = !!enrolledSession?.studentId;
      
      // Redirect if NOT logged in (covers both logout and page refresh without login)
      if (!isEnrolledLoggedIn || !hasEnrolledSession) {
        console.log("[EnrolledDashboard] No valid session, redirecting to signin", { isEnrolledLoggedIn, hasEnrolledSession });
        clearEnrolledSession();
        navigate("/enrolled/signin", { replace: true });
      }
    }
  }, [navigate, loading]);

  // --- UI / modal state used by the dashboard header/profile areas ---
  const [announcement, setAnnouncement] = useState("Welcome to EnrollEase");
  const [announcementSettings, setAnnouncementSettings] = useState(null);
  const [enrolledAnnouncementText, setEnrolledAnnouncementText] = useState("");

  const formatDate = (dateStr) => {
    return formatCalendarDate(dateStr);
  };

  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "announcement");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAnnouncementSettings(data);
        setAnnouncement(data.announcement || "");

      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const { active } = buildEnrollmentWindows(announcementSettings || {}, todayKey);
    if (!active) {
      setEnrolledAnnouncementText("");
      return;
    }
    const range = active.year ? `AY ${active.year}` : "AY";
    setEnrolledAnnouncementText(
      `Enrollment for ${range} ${active.semester} will be open from ${formatDate(active.start)} to ${formatDate(active.end)}.`
    );
  }, [announcementSettings, todayKey]);

  // Edit profile modal / upload
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [editMsg, setEditMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePassMsg, setChangePassMsg] = useState("");

  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editInfoSaving, setEditInfoSaving] = useState(false);
  const [editInfoError, setEditInfoError] = useState("");
  const [editInfoForm, setEditInfoForm] = useState({
    category: "",
    program: "",
    firstName: "",
    middleName: "",
    middleNameNA: false,
    lastName: "",
    gender: "",
    dob: "",
    mobile: "",
    email: "",
    pictureUrl: "",
    birthCertUrl: "",
    schoolIdUrl: "",
    gradesUrl: "",
    pictureHardcopy: false,
    birthCertHardcopy: false,
    schoolIdHardcopy: false,
    gradesHardcopy: false,
    preRegistration: {
      entryLevel: "",
      semester: "",
      nationality: "",
      religion: "",
      civilStatus: "",
      birthPlace: "",
      present: { house: "", barangay: "", city: "", province: "", postal: "", country: "" },
      permanent: { house: "", barangay: "", city: "", province: "", postal: "", country: "" },
      sameAsPresent: false,
      elementary: { school: "", address: "", year: "" },
      junior: { school: "", address: "", year: "" },
      senior: { school: "", address: "", year: "", strand: "" },
      parentName: "",
      relationship: "",
      occupation: "",
      familyAddress: "",
      familyMobile: "",
    }
  });

  const [editInfoFiles, setEditInfoFiles] = useState({
    picture: null,
    birthCert: null,
    schoolId: null,
    grades: null,
  });
  const [editInfoFieldErrors, setEditInfoFieldErrors] = useState({});

  const isValidEmail = (val) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val);
  const isValidMobile = (val) => /^09\d{9}$/.test(val);
  const getFileErrorMessage = (file) => {
    if (!file) return "";
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) return "Only JPG, PNG, or PDF files are allowed.";
    if (file.size > maxSize) return `File size must be 5MB or below (${(file.size / 1024 / 1024).toFixed(2)}MB).`;
    return "";
  };

  const currentYear = new Date().getFullYear();
  const isAlphaSpace = (val) => typeof val === 'string' && /^[A-Za-z\s]+$/.test(val);
  const isValidYear = (val) => typeof val === 'string' && /^\d{4}$/.test(val) && Number(val) >= 1920 && Number(val) <= currentYear;
  const processAlphaText = (val, max = 50) => {
    if (typeof val !== 'string') return val;
    return val.replace(/[^A-Za-z\s]/g, '').replace(/\s+/g, ' ').trim().slice(0, max);
  };

  const getEditFieldError = (path) => {
    if (!editInfoFieldErrors) return "";
    const keys = path.split('.');
    const last = keys[keys.length - 1];
    const simple = last;
    const pathKey = path;
    let combined = '';
    if (keys.length >= 2) {
      const parent = keys[keys.length - 2];
      combined = `${parent}${last.charAt(0).toUpperCase()}${last.slice(1)}`;
    }
    return editInfoFieldErrors[simple] || editInfoFieldErrors[pathKey] || editInfoFieldErrors[combined] || "";
  };

  const validateEditInfoForm = () => {
    const errors = {};
    const value = (val) => (typeof val === 'string' ? val.trim() : '');

    if (!value(editInfoForm.category)) errors.category = 'Category is required.';
    if (!value(editInfoForm.program)) errors.program = 'Program is required.';
    if (!value(editInfoForm.firstName)) errors.firstName = 'First name is required.';
    if (!value(editInfoForm.lastName)) errors.lastName = 'Last name is required.';
    if (!value(editInfoForm.gender)) errors.gender = 'Gender is required.';
    if (!value(editInfoForm.dob)) errors.dob = 'Date of birth is required.';
    if (!value(editInfoForm.mobile)) {
      errors.mobile = 'Mobile number is required.';
    } else if (!isValidMobile(value(editInfoForm.mobile))) {
      errors.mobile = 'Enter a valid mobile number starting with 09 and containing 11 digits.';
    }
    if (!value(editInfoForm.email)) {
      errors.email = 'Email is required.';
    } else if (!isValidEmail(value(editInfoForm.email))) {
      errors.email = 'Enter a valid email address.';
    }

    if (!value(editInfoForm.preRegistration.entryLevel)) errors.entryLevel = 'Entry Level is required.';
    if (!value(editInfoForm.preRegistration.semester)) errors.semester = 'Semester is required.';
    if (!value(editInfoForm.preRegistration.nationality)) errors.nationality = 'Nationality is required.';
    if (!value(editInfoForm.preRegistration.religion)) errors.religion = 'Religion is required.';
    if (!value(editInfoForm.preRegistration.civilStatus)) errors.civilStatus = 'Civil status is required.';
    if (!value(editInfoForm.preRegistration.birthPlace)) errors.birthPlace = 'Birth place is required.';
    if (value(editInfoForm.preRegistration.nationality) && !isAlphaSpace(editInfoForm.preRegistration.nationality)) errors.nationality = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.religion) && !isAlphaSpace(editInfoForm.preRegistration.religion)) errors.religion = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.birthPlace) && !isAlphaSpace(editInfoForm.preRegistration.birthPlace)) errors.birthPlace = 'Invalid characters - letters only';

    if (!value(editInfoForm.preRegistration.present.house)) errors.presentHouse = 'Present house/street is required.';
    if (!value(editInfoForm.preRegistration.present.barangay)) errors.presentBarangay = 'Present barangay is required.';
    if (!value(editInfoForm.preRegistration.present.city)) errors.presentCity = 'Present city is required.';
    if (!value(editInfoForm.preRegistration.present.province)) errors.presentProvince = 'Present province is required.';
    if (!value(editInfoForm.preRegistration.present.postal)) errors.presentPostal = 'Present postal code is required.';
    if (!value(editInfoForm.preRegistration.present.country)) errors.presentCountry = 'Present country is required.';
    if (value(editInfoForm.preRegistration.present.city) && !isAlphaSpace(editInfoForm.preRegistration.present.city)) errors.presentCity = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.present.province) && !isAlphaSpace(editInfoForm.preRegistration.present.province)) errors.presentProvince = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.present.country) && !isAlphaSpace(editInfoForm.preRegistration.present.country)) errors.presentCountry = 'Invalid characters - letters only';

    if (!editInfoForm.preRegistration.sameAsPresent) {
      if (!value(editInfoForm.preRegistration.permanent.house)) errors.permanentHouse = 'Permanent house/street is required.';
      if (!value(editInfoForm.preRegistration.permanent.barangay)) errors.permanentBarangay = 'Permanent barangay is required.';
      if (!value(editInfoForm.preRegistration.permanent.city)) errors.permanentCity = 'Permanent city is required.';
      if (!value(editInfoForm.preRegistration.permanent.province)) errors.permanentProvince = 'Permanent province is required.';
      if (!value(editInfoForm.preRegistration.permanent.postal)) errors.permanentPostal = 'Permanent postal code is required.';
      if (!value(editInfoForm.preRegistration.permanent.country)) errors.permanentCountry = 'Permanent country is required.';
      if (value(editInfoForm.preRegistration.permanent.city) && !isAlphaSpace(editInfoForm.preRegistration.permanent.city)) errors.permanentCity = 'Invalid characters - letters only';
      if (value(editInfoForm.preRegistration.permanent.province) && !isAlphaSpace(editInfoForm.preRegistration.permanent.province)) errors.permanentProvince = 'Invalid characters - letters only';
      if (value(editInfoForm.preRegistration.permanent.country) && !isAlphaSpace(editInfoForm.preRegistration.permanent.country)) errors.permanentCountry = 'Invalid characters - letters only';
    }

    if (!value(editInfoForm.preRegistration.familyAddress)) errors.familyAddress = 'Family address is required.';
    if (!value(editInfoForm.preRegistration.familyMobile)) {
      errors.familyMobile = 'Family mobile number is required.';
    } else if (!/^[0-9]{10,11}$/.test(editInfoForm.preRegistration.familyMobile.replace(/\D/g, ''))) {
      errors.familyMobile = 'Enter a valid family mobile number with 10 or 11 digits.';
    }

    if (value(editInfoForm.preRegistration.elementary.school) && !isAlphaSpace(editInfoForm.preRegistration.elementary.school)) errors.elementarySchool = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.junior.school) && !isAlphaSpace(editInfoForm.preRegistration.junior.school)) errors.juniorSchool = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.senior.school) && !isAlphaSpace(editInfoForm.preRegistration.senior.school)) errors.seniorSchool = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.parentName) && !isAlphaSpace(editInfoForm.preRegistration.parentName)) errors.parentName = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.occupation) && !isAlphaSpace(editInfoForm.preRegistration.occupation)) errors.occupation = 'Invalid characters - letters only';
    if (value(editInfoForm.preRegistration.senior.strand) && !isAlphaSpace(editInfoForm.preRegistration.senior.strand)) errors.seniorStrand = 'Invalid characters - letters only';

    if (value(editInfoForm.preRegistration.elementary.year) && !isValidYear(String(editInfoForm.preRegistration.elementary.year))) errors.elementaryYear = 'Enter a valid year';
    if (value(editInfoForm.preRegistration.junior.year) && !isValidYear(String(editInfoForm.preRegistration.junior.year))) errors.juniorYear = 'Enter a valid year';
    if (value(editInfoForm.preRegistration.senior.year) && !isValidYear(String(editInfoForm.preRegistration.senior.year))) errors.seniorYear = 'Enter a valid year';

    const attachments = [
      { field: 'picture', label: '2x2 picture' },
      { field: 'birthCert', label: 'Birth/Marriage Certificate' },
      { field: 'schoolId', label: 'School ID/Government ID' },
      { field: 'grades', label: 'Copy of Grades' },
    ];

    attachments.forEach(({ field, label }) => {
      const url = editInfoForm[`${field}Url`];
      const hardcopy = editInfoForm[`${field}Hardcopy`];
      const file = editInfoFiles[field];
      if (!url && !hardcopy && !file) {
        errors[field] = `Provide ${label} or choose hard copy later.`;
      } else if (file) {
        const fileError = getFileErrorMessage(file);
        if (fileError) errors[field] = fileError;
      }
    });

    setEditInfoFieldErrors(errors);
    return errors;
  };

  const handleEditInfoFile = (field, file) => {
    if (!file) return;
    const error = getFileErrorMessage(file);
    setEditInfoFieldErrors((prev) => ({ ...prev, [field]: error }));
    setEditInfoFiles((prev) => ({ ...prev, [field]: error ? null : file }));
  };

  const handleToggleHardcopy = (field, checked) => {
    const nextValue = !!checked;
    setEditInfoForm((prev) => ({ ...prev, [`${field}Hardcopy`]: nextValue }));
    if (nextValue) {
      setEditInfoFiles((prev) => ({ ...prev, [field]: null }));
    }
  };

  const openEditInfoModal = () => {
    const source = localStudent || student || {};
    const pre = source.preRegistration || {};
    setEditInfoForm({
      category: source.category || "",
      program: source.program || "",
      firstName: source.firstName || "",
      middleName: source.middleName || "",
      middleNameNA: !!source.middleNameNA,
      lastName: source.lastName || "",
      gender: source.gender || "",
      dob: source.dob || "",
      mobile: source.mobile || "",
      email: source.email || "",
      pictureUrl: source.picture || "",
      birthCertUrl: source.birthCert || "",
      schoolIdUrl: source.schoolId || "",
      gradesUrl: source.grades || "",
      pictureHardcopy: !!source.pictureHardcopy,
      birthCertHardcopy: !!source.birthCertHardcopy,
      schoolIdHardcopy: !!source.schoolIdHardcopy,
      gradesHardcopy: !!source.gradesHardcopy,
      preRegistration: {
        entryLevel: pre.entryLevel || "",
        semester: pre.semester || "",
        nationality: pre.nationality || "",
        religion: pre.religion || "",
        civilStatus: pre.civilStatus || "",
        birthPlace: pre.birthPlace || "",
        present: {
          house: pre.present?.house || "",
          barangay: pre.present?.barangay || "",
          city: pre.present?.city || "",
          province: pre.present?.province || "",
          postal: pre.present?.postal || "",
          country: pre.present?.country || "",
        },
        permanent: {
          house: pre.permanent?.house || "",
          barangay: pre.permanent?.barangay || "",
          city: pre.permanent?.city || "",
          province: pre.permanent?.province || "",
          postal: pre.permanent?.postal || "",
          country: pre.permanent?.country || "",
        },
        sameAsPresent: !!pre.sameAsPresent,
        elementary: {
          school: pre.elementary?.school || "",
          address: pre.elementary?.address || "",
          year: pre.elementary?.year || "",
        },
        junior: {
          school: pre.junior?.school || "",
          address: pre.junior?.address || "",
          year: pre.junior?.year || "",
        },
        senior: {
          school: pre.senior?.school || "",
          address: pre.senior?.address || "",
          year: pre.senior?.year || "",
          strand: pre.senior?.strand || "",
        },
        parentName: pre.parentName || "",
        relationship: pre.relationship || "",
        occupation: pre.occupation || "",
        familyAddress: pre.familyAddress || "",
        familyMobile: pre.familyMobile || "",
      }
    });
    setEditInfoFiles({ picture: null, birthCert: null, schoolId: null, grades: null });
    setEditInfoFieldErrors({});
    setEditInfoError("");
    setEditInfoOpen(true);
  };

  const handleEditInfoField = (path, value) => {
    const alphaPaths = new Set([
      'preRegistration.nationality', 'preRegistration.religion', 'preRegistration.birthPlace',
      'preRegistration.present.city', 'preRegistration.present.province', 'preRegistration.present.country',
      'preRegistration.permanent.city', 'preRegistration.permanent.province', 'preRegistration.permanent.country',
      'preRegistration.elementary.school', 'preRegistration.junior.school', 'preRegistration.senior.school',
      'preRegistration.senior.strand', 'preRegistration.parentName', 'preRegistration.occupation'
    ]);
    if (alphaPaths.has(path)) value = processAlphaText(String(value || ''));
    if (path.endsWith('.year')) value = String(value || '').replace(/\D/g, '').slice(0, 4);

    setEditInfoForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let target = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        const key = keys[i];
        target[key] = { ...target[key] };
        target = target[key];
      }
      target[keys[keys.length - 1]] = value;
      return next;
    });
    const keys = path.split('.');
    const key = keys.slice(-1)[0];
    const combinedKey = keys.length >= 2 ? `${keys[keys.length - 2]}${key.charAt(0).toUpperCase()}${key.slice(1)}` : null;
    setEditInfoFieldErrors((prev) => ({ ...prev, [key]: "", [path]: "", ...(combinedKey ? { [combinedKey]: "" } : {}) }));
  };

  const resolveStudentDocRef = async (db) => {
    const docId = localStudent?.id || student?.id;
    if (docId) {
      const directRef = doc(db, 'students', docId);
      const directSnap = await getDoc(directRef);
      if (directSnap.exists()) return directRef;
    }
    if (student?.idNumber) {
      const q = query(collection(db, 'students'), where('idNumber', '==', student.idNumber), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].ref;
    }
    return null;
  };

  const saveEditInfo = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setEditInfoSaving(true);
    setEditInfoError("");
    try {
      const fieldErrors = validateEditInfoForm();
      if (Object.keys(fieldErrors).length > 0) {
        setEditInfoError('Please fix the highlighted fields before saving.');
        return;
      }

      const source = localStudent || student || {};
      const db = getFirestore(app);
      const storage = getStorage(app);
      const docId = source.id;

      const studentStorageKey = docId || source.idNumber || source.studentId || 'student';
      const attachmentFields = ['picture', 'birthCert', 'schoolId', 'grades'];
      const attachmentUpdates = {};

      for (const field of attachmentFields) {
        const file = editInfoFiles[field];
        if (file) {
          const fileRef = storageRef(storage, `students_uploads/${studentStorageKey}/${field}_${Date.now()}_${file.name}`);
          await uploadBytes(fileRef, file);
          attachmentUpdates[`${field}Url`] = await getDownloadURL(fileRef);
        }
      }

      const payload = {
        category: editInfoForm.category,
        program: editInfoForm.program,
        firstName: editInfoForm.firstName,
        middleName: editInfoForm.middleName,
        middleNameNA: !!editInfoForm.middleNameNA,
        lastName: editInfoForm.lastName,
        gender: editInfoForm.gender,
        dob: editInfoForm.dob,
        mobile: editInfoForm.mobile,
        email: editInfoForm.email,
        picture: attachmentUpdates.pictureUrl || editInfoForm.pictureUrl || "",
        birthCert: attachmentUpdates.birthCertUrl || editInfoForm.birthCertUrl || "",
        schoolId: attachmentUpdates.schoolIdUrl || editInfoForm.schoolIdUrl || "",
        grades: attachmentUpdates.gradesUrl || editInfoForm.gradesUrl || "",
        pictureHardcopy: !!editInfoForm.pictureHardcopy,
        birthCertHardcopy: !!editInfoForm.birthCertHardcopy,
        schoolIdHardcopy: !!editInfoForm.schoolIdHardcopy,
        gradesHardcopy: !!editInfoForm.gradesHardcopy,
        preRegistration: editInfoForm.preRegistration,
        updatedAt: new Date().toISOString(),
      };

      if (!payload.firstName || !payload.lastName || !payload.program || !payload.category) {
        throw new Error("Please complete first name, last name, program, and category.");
      }

      if (docId) {
        await updateDoc(doc(db, "students", docId), payload);
      } else if (source.studentId) {
        const q = query(collection(db, "students"), where("studentId", "==", source.studentId), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) await updateDoc(snap.docs[0].ref, payload);
      }

      const updatedStudent = { ...source, ...payload };
      setLocalStudent(updatedStudent);
      writeEnrolledStudentData(updatedStudent);
      setEditInfoOpen(false);
    } catch (err) {
      console.error("Failed to update student info:", err);
      setEditInfoError(err.message || "Unable to update student information.");
    } finally {
      setEditInfoSaving(false);
    }
  };

  const handleToggleSameAsPresent = (checked) => {
    handleEditInfoField('preRegistration.sameAsPresent', checked);
    if (checked) {
      const present = editInfoForm.preRegistration.present;
      handleEditInfoField('preRegistration.permanent', { ...present });
    }
  };

  const handleEditInfoClose = () => {
    setEditInfoOpen(false);
    setEditInfoError("");
  };

  const saveProfile = async () => {
    setUploading(true);
    setEditMsg("");
    try {
      const db = getFirestore(app);
      const storage = getStorage(app);
      const source = localStudent || student || {};
      const docId = source.id;
      const targetId = docId || source.studentId || source.idNumber || "student";
      let updatedStudent = { ...source };

      if (editFile) {
        const fileRef = storageRef(storage, `students_uploads/${targetId}/profile_${Date.now()}_${editFile.name}`);
        await uploadBytes(fileRef, editFile);
        updatedStudent.profilePicUrl = await getDownloadURL(fileRef);
      }

      if (docId) {
        await updateDoc(doc(db, "students", docId), {
          profilePicUrl: updatedStudent.profilePicUrl || source.profilePicUrl || "",
          updatedAt: new Date().toISOString(),
        });
      } else if (source.studentId || source.idNumber) {
        const q = query(collection(db, "students"), where(source.studentId ? "studentId" : "idNumber", "==", source.studentId || source.idNumber), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, {
            profilePicUrl: updatedStudent.profilePicUrl || source.profilePicUrl || "",
            updatedAt: new Date().toISOString(),
          });
        }
      }

      setLocalStudent(updatedStudent);
      writeEnrolledStudentData(updatedStudent);
      setEditProfileOpen(false);
      setEditMsg("Profile saved successfully.");
      setPreviewUrl("");
      setEditFile(null);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setEditMsg("Failed to save profile.");
    } finally {
      setUploading(false);
    }
  };

  const changePasswordNow = async () => {
    if (!currentPassword || !newPassword) {
      setChangePassMsg("Please fill in all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangePassMsg("New password and confirmation do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setChangePassMsg("Password should be at least 6 characters.");
      return;
    }
    try {
      const studentId = localStudent?.id || student?.id;
      if (!studentId) throw new Error("No enrolled student session found.");
      const studentRef = doc(getFirestore(app), "students", studentId);
      const studentSnap = await getDoc(studentRef);
      if (!studentSnap.exists()) throw new Error("No enrolled student session found.");
      const studentData = studentSnap.data() || {};
      const passwordField = Object.prototype.hasOwnProperty.call(studentData, "studentpassword")
        ? "studentpassword"
        : "password";
      if (studentData[passwordField] !== currentPassword) {
        throw new Error("Current password is incorrect.");
      }
      await updateDoc(studentRef, { [passwordField]: newPassword });

      setChangePassMsg("Password changed successfully.");
      setTimeout(() => {
        setChangePassOpen(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setChangePassMsg("");
      }, 1200);
    } catch (err) {
      console.error("Failed to change password:", err);
      setChangePassMsg(err.message || "Failed to change password.");
    }
  };

  // layout constants & profile menu
  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1100;
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const fullName = `${(localStudent?.firstName || student?.firstName) || ""} ${(localStudent?.lastName || student?.lastName) || ""}`.trim();
  const profilePicUrl = (localStudent?.profilePicUrl || student?.profilePicUrl) || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Enrolled")}`;
  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      sessionStorage.removeItem("currentStudentSession");
      console.log("[EnrolledDashboard] Logout: cleared all enrolled portal flags");
      navigate("/enrolled/signin", { replace: true });
      return;
    }
    if (action === "editInfo") return openEditInfoModal();
    if (action === "edit") return setEditProfileOpen(true);
    if (action === "password") return setChangePassOpen(true);
  };
  
  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);
  
  // Layout wrapper: replace the original page-level return with this consistent layout.
  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = window.location.pathname.startsWith(n.path);
            return (
              <div
                key={n.key}
                onClick={() => navigate(n.path, { state: { student: localStudent || student } })}
                style={{
                  padding: "14px 24px",
                  margin: "4px 16px",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: isActive ? "#ff6f2e" : "transparent",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s",
                  boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
                }}
              >
                {n.label}
              </div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999 }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Announcement</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 210, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#222", fontWeight: 500 }} onClick={() => handleProfile("editInfo")}>Edit Information</div>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#222", fontWeight: 500 }} onClick={() => handleProfile("edit")}>Edit Profile</div>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#222", fontWeight: 500 }} onClick={() => handleProfile("password")}>Change Password</div>
                <div style={{ height: 1, background: "#eee", margin: "6px 0" }} />
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto" }} data-scroll-container>
          <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto", height: "100%" }}>
            {/* Information Section (copied from StudentDashboard) */}
            <div style={{ background: '#f8fafb', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', marginBottom: 24, border: '1px solid #e5e7eb' }}>
              <div style={{ background: '#e8e8e8', color: '#0f5f3b', fontWeight: 700, fontSize: 22, padding: '12px 24px', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>Information</div>
              <div style={{ padding: '24px 32px', fontSize: 16, color: '#222' }}>
                <div style={{ marginBottom: 6 }}><b>Full Name:</b> {localStudent?.firstName || student.firstName}, {localStudent?.lastName || student.lastName}</div>
                <div style={{ marginBottom: 6 }}><b>Student ID:</b> {localStudent?.studentId || student.studentId}</div>
                <div style={{ marginBottom: 6 }}><b>Program:</b> {localStudent?.program || student.program}</div>
                <div><b>Category:</b> {localStudent?.category || student.category || '-'}</div>
              </div>
            </div>

            {/* Announcement Section (copied from StudentDashboard) */}
            <div style={{ background: '#f8fafb', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', border: '1px solid #e5e7eb' }}>
              <div style={{ background: '#e8e8e8', color: '#0f5f3b', fontWeight: 700, fontSize: 22, padding: '12px 24px', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>Announcement</div>
              <div style={{ padding: '32px 32px 16px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 180, position: 'relative' }}>
                {enrolledAnnouncementText ? (
                  <div style={{ background: '#fffbe6', color: '#b26a00', border: '1px solid #ffe58f', borderRadius: 8, padding: '16px 24px', fontWeight: 600, fontSize: 17, textAlign: 'center', marginBottom: 24 }}>
                    {enrolledAnnouncementText}
                  </div>
                ) : null}
              </div>
            </div>
            {/* Edit information modal */}
            {editInfoOpen && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <form className="edit-info-modal" onSubmit={saveEditInfo} style={{ width: '100%', maxWidth: 1100, maxHeight: '90vh', overflowY: 'auto', overflowX: 'auto', background: '#fff', borderRadius: 16, padding: 28, boxSizing: 'border-box', boxShadow: '0 16px 48px rgba(0,0,0,0.18)' }}>
                  <style>{`.edit-info-modal label { display: block; margin-bottom: 10px; min-width: 0; }
                  .edit-info-modal input[type=text], .edit-info-modal input[type=email], .edit-info-modal input[type=date], .edit-info-modal select, .edit-info-modal input[type=file] { width: 100%; padding: 10px; margin-top: 6px; border-radius: 6px; box-sizing: border-box; min-height: 40px; font-size: 14px; border: 1px solid #ccc; }
                  .edit-info-modal input[type=checkbox] { width: auto; margin-right: 8px; }
                  .edit-info-modal input[readonly] { background: #f7f7f7; color: #111; }
                  .edit-info-modal .error-field { border: 2px solid red !important; }
                  .edit-info-modal .section-panel { min-width: 0; }
                  .edit-info-modal .grid-fit { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 24px; width: 100%; }
                  .edit-info-modal .section-panel-inner { background: #f8fafb; border-radius: 12px; border: 1px solid #e5e7eb; padding: 18px; min-width: 0; }
                  .edit-info-modal .inline-label { display: inline-flex; align-items: center; gap: 8px; }`}</style>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ margin: 0, fontSize: 24, color: '#155c2b' }}>Edit Student Information</h3>
                      <div style={{ marginTop: 4, color: '#666', fontSize: 14 }}>Update application and pre-registration data, then click Save to save.</div>
                    </div>
                    <button type="button" onClick={handleEditInfoClose} style={{ background: 'transparent', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#444' }}>×</button>
                  </div>

                  <div className="grid-fit" style={{ marginBottom: 24 }}>
                    <div className="section-panel-inner">
                      <div style={{ fontWeight: 700, marginBottom: 10 }}>Application Details</div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Category
                        <input type="text" readOnly value={editInfoForm.category || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Program
                        <input type="text" readOnly value={editInfoForm.program || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        First Name
                        <input type="text" value={editInfoForm.firstName} onChange={(e) => handleEditInfoField('firstName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('firstName') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('firstName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('firstName')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Middle Name
                        <input type="text" value={editInfoForm.middleName} onChange={(e) => handleEditInfoField('middleName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('middleName') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('middleName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('middleName')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        <input type="checkbox" checked={editInfoForm.middleNameNA} onChange={(e) => handleEditInfoField('middleNameNA', e.target.checked)} style={{ marginRight: 8 }} />
                        Middle Name N/A
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Last Name
                        <input type="text" value={editInfoForm.lastName} onChange={(e) => handleEditInfoField('lastName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('lastName') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('lastName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('lastName')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Gender
                        <select value={editInfoForm.gender} onChange={(e) => handleEditInfoField('gender', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('gender') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }}>
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                        {getEditFieldError('gender') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('gender')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Date of Birth
                        <input type="date" value={editInfoForm.dob} onChange={(e) => handleEditInfoField('dob', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('dob') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('dob') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('dob')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Mobile Number
                        <input type="text" value={editInfoForm.mobile} onChange={(e) => handleEditInfoField('mobile', e.target.value)} placeholder="09XXXXXXXXX" style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('mobile') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('mobile') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('mobile')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Email
                        <input type="email" value={editInfoForm.email} onChange={(e) => handleEditInfoField('email', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('email') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('email') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('email')}</div>}
                      </label>
                    </div>

                    <div className="section-panel-inner" style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, marginBottom: 10 }}>Pre-Registration</div>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Entry Level
                        <input type="text" readOnly value={editInfoForm.preRegistration.entryLevel || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Semester
                        <input type="text" readOnly value={editInfoForm.preRegistration.semester || ''} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: '#f7f7f7', color: '#111' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Nationality
                        <input type="text" value={editInfoForm.preRegistration.nationality} onChange={(e) => handleEditInfoField('preRegistration.nationality', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.nationality') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('preRegistration.nationality') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.nationality')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Religion
                        <input type="text" value={editInfoForm.preRegistration.religion} onChange={(e) => handleEditInfoField('preRegistration.religion', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.religion') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('preRegistration.religion') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.religion')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Civil Status
                        <input type="text" value={editInfoForm.preRegistration.civilStatus} onChange={(e) => handleEditInfoField('preRegistration.civilStatus', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Birth Place
                        <input type="text" value={editInfoForm.preRegistration.birthPlace} onChange={(e) => handleEditInfoField('preRegistration.birthPlace', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.birthPlace') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                        {getEditFieldError('preRegistration.birthPlace') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.birthPlace')}</div>}
                      </label>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Address</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, width: '100%' }}>
                      <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>Present Address</div>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          House/Street
                          <input type="text" value={editInfoForm.preRegistration.present.house} onChange={(e) => handleEditInfoField('preRegistration.present.house', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Barangay
                          <input type="text" value={editInfoForm.preRegistration.present.barangay} onChange={(e) => handleEditInfoField('preRegistration.present.barangay', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          City
                          <input type="text" value={editInfoForm.preRegistration.present.city} onChange={(e) => handleEditInfoField('preRegistration.present.city', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.present.city') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                          {getEditFieldError('preRegistration.present.city') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.present.city')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Province
                          <input type="text" value={editInfoForm.preRegistration.present.province} onChange={(e) => handleEditInfoField('preRegistration.present.province', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.present.province') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                          {getEditFieldError('preRegistration.present.province') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.present.province')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Postal Code
                          <input type="text" value={editInfoForm.preRegistration.present.postal} onChange={(e) => handleEditInfoField('preRegistration.present.postal', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Country
                          <input type="text" value={editInfoForm.preRegistration.present.country} onChange={(e) => handleEditInfoField('preRegistration.present.country', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.present.country') ? '2px solid red' : '1px solid #ccc', boxSizing: 'border-box', height: 40 }} />
                          {getEditFieldError('preRegistration.present.country') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.present.country')}</div>}
                        </label>
                      </div>
                      <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div style={{ fontWeight: 700 }}>Permanent Address</div>
                          <label style={{ fontSize: 13, color: '#333' }}>
                            <input type="checkbox" checked={editInfoForm.preRegistration.sameAsPresent} onChange={(e) => handleToggleSameAsPresent(e.target.checked)} style={{ marginRight: 8 }} /> Same as present
                          </label>
                        </div>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          House/Street
                          <input type="text" value={editInfoForm.preRegistration.permanent.house} onChange={(e) => handleEditInfoField('preRegistration.permanent.house', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Barangay
                          <input type="text" value={editInfoForm.preRegistration.permanent.barangay} onChange={(e) => handleEditInfoField('preRegistration.permanent.barangay', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          City
                          <input type="text" value={editInfoForm.preRegistration.permanent.city} onChange={(e) => handleEditInfoField('preRegistration.permanent.city', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.permanent.city') ? '2px solid red' : '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                          {getEditFieldError('preRegistration.permanent.city') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.permanent.city')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Province
                          <input type="text" value={editInfoForm.preRegistration.permanent.province} onChange={(e) => handleEditInfoField('preRegistration.permanent.province', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.permanent.province') ? '2px solid red' : '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                          {getEditFieldError('preRegistration.permanent.province') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.permanent.province')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Postal Code
                          <input type="text" value={editInfoForm.preRegistration.permanent.postal} onChange={(e) => handleEditInfoField('preRegistration.permanent.postal', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Country
                          <input type="text" value={editInfoForm.preRegistration.permanent.country} onChange={(e) => handleEditInfoField('preRegistration.permanent.country', e.target.value)} disabled={editInfoForm.preRegistration.sameAsPresent} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.permanent.country') ? '2px solid red' : '1px solid #ccc', background: editInfoForm.preRegistration.sameAsPresent ? '#f5f5f5' : '#fff' }} />
                          {getEditFieldError('preRegistration.permanent.country') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.permanent.country')}</div>}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Education</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                      <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>Elementary</div>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          School
                          <input type="text" value={editInfoForm.preRegistration.elementary.school} onChange={(e) => handleEditInfoField('preRegistration.elementary.school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.elementary.school') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.elementary.school') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.elementary.school')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Address
                          <input type="text" value={editInfoForm.preRegistration.elementary.address} onChange={(e) => handleEditInfoField('preRegistration.elementary.address', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Year
                          <input type="text" value={editInfoForm.preRegistration.elementary.year} onChange={(e) => handleEditInfoField('preRegistration.elementary.year', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.elementary.year') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.elementary.year') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.elementary.year')}</div>}
                        </label>
                      </div>
                      <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>Junior High School</div>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          School
                          <input type="text" value={editInfoForm.preRegistration.junior.school} onChange={(e) => handleEditInfoField('preRegistration.junior.school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.junior.school') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.junior.school') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.junior.school')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Address
                          <input type="text" value={editInfoForm.preRegistration.junior.address} onChange={(e) => handleEditInfoField('preRegistration.junior.address', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Year
                          <input type="text" value={editInfoForm.preRegistration.junior.year} onChange={(e) => handleEditInfoField('preRegistration.junior.year', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.junior.year') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.junior.year') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.junior.year')}</div>}
                        </label>
                      </div>
                      <div style={{ minWidth: 0, background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                        <div style={{ fontWeight: 700, marginBottom: 12 }}>Senior High School</div>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          School
                          <input type="text" value={editInfoForm.preRegistration.senior.school} onChange={(e) => handleEditInfoField('preRegistration.senior.school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.senior.school') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.senior.school') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.senior.school')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Address
                          <input type="text" value={editInfoForm.preRegistration.senior.address} onChange={(e) => handleEditInfoField('preRegistration.senior.address', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: '1px solid #ccc' }} />
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Year
                          <input type="text" value={editInfoForm.preRegistration.senior.year} onChange={(e) => handleEditInfoField('preRegistration.senior.year', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.senior.year') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.senior.year') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.senior.year')}</div>}
                        </label>
                        <label style={{ display: 'block', marginBottom: 10 }}>
                          Strand
                          <input type="text" value={editInfoForm.preRegistration.senior.strand} onChange={(e) => handleEditInfoField('preRegistration.senior.strand', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.senior.strand') ? '2px solid red' : '1px solid #ccc' }} />
                          {getEditFieldError('preRegistration.senior.strand') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.senior.strand')}</div>}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Family Information</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(280px, 1fr))', gap: 24, width: '100%' }}>
                      <label style={{ display: 'block', marginBottom: 10, minWidth: 0 }}>
                        Parent/Guardian Name
                        <input type="text" value={editInfoForm.preRegistration.parentName} onChange={(e) => handleEditInfoField('preRegistration.parentName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.parentName') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.parentName') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.parentName')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Relationship
                        <input type="text" value={editInfoForm.preRegistration.relationship} onChange={(e) => handleEditInfoField('preRegistration.relationship', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.relationship') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.relationship') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.relationship')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Occupation
                        <input type="text" value={editInfoForm.preRegistration.occupation} onChange={(e) => handleEditInfoField('preRegistration.occupation', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.occupation') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.occupation') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.occupation')}</div>}
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 3fr', gap: 24, width: '100%', alignItems: 'start' }}>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Family Mobile
                        <input type="text" value={editInfoForm.preRegistration.familyMobile} onChange={(e) => handleEditInfoField('preRegistration.familyMobile', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.familyMobile') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.familyMobile') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.familyMobile')}</div>}
                      </label>
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        Family Address
                        <input type="text" value={editInfoForm.preRegistration.familyAddress} onChange={(e) => handleEditInfoField('preRegistration.familyAddress', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 6, border: getEditFieldError('preRegistration.familyAddress') ? '2px solid red' : '1px solid #ccc' }} />
                        {getEditFieldError('preRegistration.familyAddress') && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{getEditFieldError('preRegistration.familyAddress')}</div>}
                      </label>
                    </div>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Attachments</div>
                    <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                      Upload attachments accept JPG, PNG, PDF type (filesize: 5MB and below). Only empty attachment slots may be filled; existing uploaded files cannot be changed here.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, width: '100%' }}>
                      {[
                        { key: 'picture', label: '2x2 Picture', hardcopyLabel: 'I will submit a physical hard copy later' },
                        { key: 'birthCert', label: 'Birth/Marriage Certificate', hardcopyLabel: 'I will submit a physical hard copy later' },
                        { key: 'schoolId', label: 'School ID/Government ID', hardcopyLabel: 'I will submit a physical hard copy later' },
                        { key: 'grades', label: 'Copy of Grades', hardcopyLabel: 'I will submit a physical hard copy later' },
                      ].map(({ key, label, hardcopyLabel }) => {
                        const hasUrl = !!editInfoForm[`${key}Url`];
                        const hardcopyChecked = editInfoForm[`${key}Hardcopy`];
                        return (
                          <div key={key} style={{ background: '#f8fafb', borderRadius: 12, border: '1px solid #e5e7eb', padding: 18 }}>
                            <label style={{ fontWeight: 600 }}>{label}</label>
                            {editInfoFieldErrors[key] && <div style={{ color: '#d32f2f', fontSize: 12, marginTop: 6 }}>{editInfoFieldErrors[key]}</div>}
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              disabled={hasUrl || hardcopyChecked}
                              onChange={(e) => handleEditInfoFile(key, e.target.files?.[0])}
                              style={{ width: '100%', marginTop: 8 }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                              {hasUrl && (
                                <span>Already uploaded. <a href={editInfoForm[`${key}Url`]} target="_blank" rel="noreferrer" style={{ color: '#155c2b' }}>View</a></span>
                              )}
                              {hardcopyChecked && !hasUrl && <span>Physical hard copy will be submitted later.</span>}
                              {!hasUrl && !hardcopyChecked && <span>Only upload if this slot is empty.</span>}
                            </div>
                            <label style={{ marginTop: 12, fontSize: 13, display: 'block' }}>
                              <input
                                type="checkbox"
                                checked={hardcopyChecked}
                                disabled={hasUrl}
                                onChange={(e) => handleToggleHardcopy(key, e.target.checked)}
                                style={{ marginRight: 8 }}
                              />
                              {hardcopyLabel}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {editInfoError && <div style={{ color: '#d32f2f', marginBottom: 18 }}>{editInfoError}</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button type="button" onClick={handleEditInfoClose} style={{ background: '#f5f5f5', color: '#333', border: '1px solid #ccc', borderRadius: 8, padding: '12px 22px', cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" disabled={editInfoSaving} style={{ background: editInfoSaving ? '#ccc' : '#155c2b', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontWeight: 700, cursor: editInfoSaving ? 'not-allowed' : 'pointer' }}>{editInfoSaving ? 'Saving...' : 'Save'}</button>
                  </div>
                </form>
              </div>
            )}
            {/* Edit profile modal */}
            {editProfileOpen && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
                <div style={{ width: 520, background: "#fff", borderRadius: 12, padding: 24 }}>
                  <h3 style={{ marginTop: 0 }}>Edit Profile</h3>
                  <div style={{ height: 1, background: "#eee", margin: "12px 0" }} />
                  <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 12 }}>
                    <div style={{ width: 96, height: 96, borderRadius: '50%', overflow: 'hidden', border: '2px solid #ff6f2e' }}>
                      <img src={previewUrl || localStudent?.profilePicUrl || profilePicUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div>
                        <label style={{ fontWeight: 500 }}>Profile picture</label>
                        <input type="file" accept="image/*" onChange={async (e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) {
                            if (f.size > 5 * 1024 * 1024) { setEditMsg('Please select an image smaller than 5 MB.'); return; }
                            setEditFile(f);
                            const url = URL.createObjectURL(f);
                            setPreviewUrl(url);
                          }
                        }} style={{ display: 'block', marginTop: 8 }} />
                        <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>Accepted: JPG/PNG. Max size 5 MB.</div>
                      </div>
                      {editMsg && <div style={{ marginTop: 8, color: editMsg.includes('success') ? 'green' : 'red' }}>{editMsg}</div>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                    <button onClick={() => { setEditProfileOpen(false); setEditFile(null); setPreviewUrl(''); setEditMsg(''); }} style={{ padding: '8px 14px' }}>Cancel</button>
                    <button onClick={saveProfile} style={{ padding: '8px 14px', background: '#0f5f3b', color: '#fff', border: 'none', borderRadius: 6 }}>{uploading ? 'Saving...' : 'Save'}</button>
                  </div>
                </div>
              </div>
            )}
            {/* Change password modal */}
            {changePassOpen && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
                <div style={{ width: 420, background: "#fff", borderRadius: 12, padding: 24 }}>
                  <h3 style={{ marginTop: 0 }}>Change password</h3>
                  <div style={{ height: 1, background: "#eee", margin: "12px 0" }} />
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontWeight: 500 }}>Current password</label>
                    <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={{ width: "100%", padding: 8, marginTop: 6 }} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontWeight: 500 }}>New password</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ width: "100%", padding: 8, marginTop: 6 }} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontWeight: 500 }}>Confirm new password</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{ width: "100%", padding: 8, marginTop: 6 }} />
                  </div>
                  {changePassMsg && <div style={{ marginTop: 12, color: changePassMsg.includes('success') ? 'green' : 'red' }}>{changePassMsg}</div>}
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                    <button onClick={() => { setChangePassOpen(false); setChangePassMsg(''); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} style={{ padding: "8px 14px" }}>Cancel</button>
                    <button onClick={changePasswordNow} disabled={changePassMsg === "Saving..."} style={{ padding: "8px 14px", background: "#0f5f3b", color: "#fff", border: "none", borderRadius: 6 }}>{changePassMsg === "Saving..." ? "Saving..." : "Change password"}</button>
                  </div>
                </div>
              </div>
            )}
           </div>
         </main>
       </div>
     </div>
   );
 }